import React from 'react';
import UniversalRouter from 'universal-router';

import { lazyWithSuspense, parseQueryString } from '@travel/utils';

import MyReviewLoader from 'components/MyReviewLoader';
import userProfileResolver from 'pages/MyReviewEdit/resolver';
import myReviewPageResolver from 'pages/MyReviewPage/resolver';
import PostReviewSkeleton from 'pages/PostReview/components/PostReviewSkeleton';
import postReviewResolver from 'pages/PostReview/resolver';
import ProviderReviewSkeleton from 'pages/ProviderReview/components/ProviderReviewSkeleton';
import providerResolver from 'pages/ProviderReview/resolver';

import { REVIEW_HOSTNAME } from 'constants/common';
import App from '../app';
import paths from './paths';
import { pageResolver } from './utils';

import type { Route } from 'universal-router';
import type { universalRouterProps } from './types';
interface ExtendedRoute extends Route {
  chunkName?: string;
  children?: Array<ExtendedRoute>;
}

const MyReviewEdit = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/MyReviewEdit" */ 'pages/MyReviewEdit'),
  {
    fallback: <MyReviewLoader />,
  },
);

const MyReviewPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/MyReviewPage" */ 'pages/MyReviewPage'),
  {
    fallback: <MyReviewLoader />,
  },
);

const PostReview = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/PostReview" */ 'pages/PostReview'),
  {
    fallback: <PostReviewSkeleton />,
  },
);

const ErrorPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ErrorPage" */ 'components/ErrorPage'),
);

const ProviderReview = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages/ProviderReview" */ 'pages/ProviderReview'),
  {
    fallback: <ProviderReviewSkeleton />,
  },
);

export default (props: universalRouterProps) => {
  const routes: Array<ExtendedRoute> = [
    {
      path: paths.top.path,
      action: async (context) => {
        const children = await context.next();

        return <App key="app">{children}</App>;
      },
      children: [
        {
          path: paths.editReviewPage.path,
          chunkName: 'pages/MyReviewEdit',
          action: async (context) => {
            await pageResolver(props, context, userProfileResolver, true);

            return <MyReviewEdit />;
          },
        },
        {
          path: [paths.myReviewPage.path, paths.otherUserReviewPage.path],
          chunkName: 'pages/MyReviewPage',
          action: async (context) => {
            await pageResolver(props, context, myReviewPageResolver, true);

            return <MyReviewPage {...context} />;
          },
        },
        {
          path: paths.reviewPostPage.path,
          chunkName: 'pages/PostReview',
          action: async (context) => {
            await pageResolver(props, context, postReviewResolver, true);

            return <PostReview {...context} />;
          },
        },
        {
          path: paths.reviewEditPage.path,
          chunkName: 'pages/PostReview',
          action: async (context) => {
            await pageResolver(props, context, postReviewResolver, true);

            return <PostReview {...context} />;
          },
        },
        {
          path: [
            paths.providerReview.path, // Rerouted HTML path
            paths.providerReview.dynamicPath, // Dynamic Path
          ],
          chunkName: 'pages/ProviderReview',
          action: async (context) => {
            const urlSearchParams = new URLSearchParams(context.search);
            const reviewId = urlSearchParams.get('reviewId');
            const isValidReviewId = reviewId ? /^[0-9]+$/.test(reviewId) : true;

            let errorStatus: number | undefined;
            let isError = false;

            const isReviewHtml =
              typeof window !== 'undefined'
                ? window.location.pathname.endsWith('review.html')
                : context.req?.url?.endsWith('review.html');
            const isReviewHost =
              typeof window !== 'undefined'
                ? window.location.hostname === REVIEW_HOSTNAME
                : context.req?.hostname === REVIEW_HOSTNAME;

            if (isReviewHtml && isReviewHost) {
              errorStatus = 404;
              isError = true;
            } else if (!isValidReviewId) {
              isError = true;
            }

            if (isError) {
              if (errorStatus) props.res?.status(errorStatus);

              return <ErrorPage />;
            }

            await pageResolver(props, context, providerResolver);

            return <ProviderReview {...context} />;
          },
        },
        {
          // also resolve paths.notFound.path
          path: '(.*)',
          chunkName: 'pages-ErrorPage',
          async action() {
            props.res?.status(400); //TODO: Need to check this and update accordingly

            return <ErrorPage />;
          },
        },
      ],
    },
  ];

  return new UniversalRouter(routes, {
    resolveRoute(context, params) {
      if (props.req) context.req = props.req;
      if (props.res) context.res = props.res;

      if (typeof context.route.action === 'function') {
        context.query = {};

        if (context.search) {
          // when redirect from omni with existing filter,
          // there will be ? added from the redirection
          const searchRegex = new RegExp('\\?$');
          const currentSearch = context.search;

          context.query = parseQueryString(
            searchRegex.test(currentSearch)
              ? currentSearch.substring(0, currentSearch.length - 1)
              : currentSearch,
          );
        }

        // Track initial chunks for SSR asset loading
        if (props.initialChunks && context.route.chunkName) {
          props.initialChunks.push(context.route.chunkName);
        }

        return context.route.action(context, params);
      }

      return undefined;
    },
  });
};
